<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { RouterLink } from "vue-router";
import type { ConsentInput, ConsentType, UserRole } from "@/types";
import { CURRENT_POLICY_VERSIONS } from "@/types";

interface Props {
  role: UserRole | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:consents": [consents: ConsentInput[]];
}>();

const checked = reactive({
  tos_privacy: false,
  ai_cv: false,
});

const isCandidate = computed(() => props.role === "candidate");

function buildConsents(): ConsentInput[] {
  const items: ConsentInput[] = [
    {
      type: "tos_privacy" as ConsentType,
      policy_version: CURRENT_POLICY_VERSIONS.tos_privacy,
      accepted: checked.tos_privacy,
    },
  ];
  if (isCandidate.value) {
    items.push({
      type: "ai_cv" as ConsentType,
      policy_version: CURRENT_POLICY_VERSIONS.ai_cv,
      accepted: checked.ai_cv,
    });
  }
  return items;
}

watch(
  () => ({ ...checked }),
  () => emit("update:consents", buildConsents()),
  { immediate: true },
);

// Expose whether all required consents are checked for parent to disable submit
const allChecked = computed(() => {
  if (isCandidate.value) return checked.tos_privacy && checked.ai_cv;
  return checked.tos_privacy;
});

defineExpose({ allChecked });
</script>

<template>
  <div class="space-y-3 pt-1">
    <!-- Checkbox 1: ToS + Privacy (all roles) -->
    <label
      class="flex items-start gap-3 cursor-pointer group"
      for="consent-tos"
    >
      <div class="relative mt-1 shrink-0">
        <input
          id="consent-tos"
          v-model="checked.tos_privacy"
          type="checkbox"
          class="sr-only peer"
        />
        <div
                    class="size-5 rounded-md border-2 border-border-standard bg-surface transition duration-150 peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 group-hover:border-primary/40 flex items-center justify-center"
        >
          <svg
            v-if="checked.tos_privacy"
            class="size-4 text-white"
            viewBox="0 0 12 10"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="1.5 5.5 4.5 8.5 10.5 1.5" />
          </svg>
        </div>
      </div>
      <span class="text-sm text-text-muted font-medium leading-relaxed">
        Saya menyetujui
        <RouterLink
          to="/terms"
          target="_blank"
          class="text-primary font-semibold hover:underline"
        >
          Syarat &amp; Ketentuan
        </RouterLink>
        dan
        <RouterLink
          to="/privacy"
          target="_blank"
          class="text-primary font-semibold hover:underline"
        >
          Kebijakan Privasi
        </RouterLink>
        Sora Recruit. <span class="text-error">*</span>
      </span>
    </label>

    <!-- Checkbox 2: AI CV (candidate only) -->
    <label
      v-if="isCandidate"
      class="flex items-start gap-3 cursor-pointer group"
      for="consent-ai"
    >
      <div class="relative mt-1 shrink-0">
        <input
          id="consent-ai"
          v-model="checked.ai_cv"
          type="checkbox"
          class="sr-only peer"
        />
        <div
                    class="size-5 rounded-md border-2 border-border-standard bg-surface transition duration-150 peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 group-hover:border-primary/40 flex items-center justify-center"
        >
          <svg
            v-if="checked.ai_cv"
            class="size-4 text-white"
            viewBox="0 0 12 10"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="1.5 5.5 4.5 8.5 10.5 1.5" />
          </svg>
        </div>
      </div>
      <span class="text-sm text-text-muted font-medium leading-relaxed">
        Saya menyetujui pemrosesan CV oleh AI (Gemini) dan transfer data sesuai
        standar privasi Sora Recruit.
        <span class="text-error">*</span>
      </span>
    </label>

    <p class="text-xs text-text-muted/60 pt-1">
      <span class="text-error">*</span> Wajib disetujui untuk melanjutkan
      pendaftaran
    </p>
  </div>
</template>
